/**
 * TelesalesDock — ngăn hồ sơ bên phải + hộp thoại (Tạo việc / Gắn cờ) + phím Esc + kéo đổi độ rộng.
 * Không chứa header / navbar. Đặt cạnh khu vực nội dung (flex row), ví dụ:
 *
 *   <div className="relative flex min-h-0 flex-1">
 *     <main className="relative min-w-0 flex-1 overflow-y-auto p-4 pb-28">
 *       <Switchboard dock={docked} />
 *       <div className="pointer-events-none absolute inset-x-3 bottom-2.5 z-30 flex"><SoftBar /></div>
 *     </main>
 *     <TelesalesDock />
 *   </div>
 *
 * `docked` (cần truyền cho Customers / Switchboard để ẩn bớt cột) lấy bằng useDocked().
 */
import React, { useEffect, useRef, useState } from 'react';
import { FlagModal, TaskModal } from './SoftBar';
import { ProfilePanel } from './ProfilePanel';
import { useMedia, useS } from './Store';
import { cx } from './UI';

/** true khi ngăn hồ sơ đang mở và màn hình ≥ xl (gắn cạnh thay vì phủ lên) */
export function useDocked() {
  const S = useS();
  const isXl = useMedia('(min-width: 1280px)');
  return S.dock.open && S.dock.tabs.length > 0 && isXl;
}

export const TeleDrawer: React.FC = () => {
  const S = useS();
  const docked = useDocked();
  const open = S.dock.open && S.dock.tabs.length > 0;
  const [drag, setDrag] = useState(false);

  const escRef = useRef(S.escClose);
  escRef.current = S.escClose;
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') escRef.current(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const setDrw = S.setDrw;
  useEffect(() => {
    if (!drag) return undefined;
    const mv = (e: PointerEvent) => setDrw(Math.max(30, Math.min(70, Math.round(((window.innerWidth - e.clientX) / window.innerWidth) * 100))));
    const up = () => setDrag(false);
    window.addEventListener('pointermove', mv);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up); };
  }, [drag, setDrw]);

  return (
    <>
      {open && (
        <aside
          aria-label="Hồ sơ khách"
          className={cx(
            'fixed inset-y-0 right-0 z-40 flex flex-col border-l-2 border-[#4091AA] bg-white text-slate-800 shadow-[-4px_0_14px_rgba(0,0,0,.12)] dark:border-[#4091AA] dark:bg-slate-900 dark:text-slate-200',
            drag && 'select-none',
          )}
          style={
            docked
              ? {
                width: `${S.drw}vw`,
                minWidth: 'min(380px, 100vw)',
                maxWidth: '100vw',
              }
              : {
                width: '100%',
                maxWidth: '560px',
              }
          }
        >
          {docked && (
            <div role="separator" aria-orientation="vertical" aria-label="Kéo để đổi độ rộng" onPointerDown={(e) => { e.preventDefault(); setDrag(true); }} className="absolute inset-y-0 -left-[5px] z-10 w-[9px] cursor-ew-resize">
              <div className="absolute left-[3px] top-1/2 -mt-5 h-10 w-[3px] rounded bg-[#7DB4C7]" />
            </div>
          )}
          <ProfilePanel docked={docked} />
        </aside>
      )}
      {S.modal?.kind === 'task' && <TaskModal key={`t-${S.modal.ma ?? ''}`} ma={S.modal.ma} />}
      {S.modal?.kind === 'flag' && S.modal.ma && <FlagModal key={`f-${S.modal.ma}`} ma={S.modal.ma} />}
    </>
  );
};

export default TeleDrawer;